import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import companies from "../data/companies";

const visitorIdStorageKey = "portfolio-visitor-id";
const sessionIdStorageKey = "portfolio-session-id";

function createStorageId(prefix) {
  const randomId =
    typeof window !== "undefined" &&
    window.crypto &&
    window.crypto.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return `${prefix}-${randomId}`;
}

function getVisitorId() {
  const fallbackId = createStorageId("visitor");

  if (typeof window === "undefined" || !window.localStorage) {
    return fallbackId;
  }

  try {
    const existingId = window.localStorage.getItem(visitorIdStorageKey);

    if (existingId) {
      return existingId;
    }

    const visitorId = createStorageId("visitor");

    window.localStorage.setItem(visitorIdStorageKey, visitorId);

    return visitorId;
  } catch (error) {
    return fallbackId;
  }
}

function getSessionId() {
  const fallbackId = createStorageId("session");

  if (typeof window === "undefined" || !window.sessionStorage) {
    return fallbackId;
  }

  try {
    const existingId = window.sessionStorage.getItem(sessionIdStorageKey);

    if (existingId) {
      return existingId;
    }

    const sessionId = createStorageId("session");

    window.sessionStorage.setItem(sessionIdStorageKey, sessionId);

    return sessionId;
  } catch (error) {
    return fallbackId;
  }
}

function createVisitId() {
  return createStorageId("visit");
}

function getTrackedRoute(pathname) {
  if (pathname === "/") {
    return {
      pagePath: "/",
      routeName: "Main Portfolio",
    };
  }

  const slug = pathname.replace(/^\/+|\/+$/g, "");
  const company = companies[slug];

  if (!company) {
    return null;
  }

  return {
    pagePath: `/${slug}`,
    routeName: company.name,
  };
}

function getBrowserDetails() {
  if (typeof window === "undefined") {
    return {};
  }

  const { document, navigator, screen } = window;

  return {
    devicePixelRatio: window.devicePixelRatio || 1,
    language: navigator.language || "",
    languages: Array.isArray(navigator.languages) ? navigator.languages : [],
    platform: navigator.platform || "",
    referrer: document.referrer || "",
    screen: {
      height: screen && screen.height,
      width: screen && screen.width,
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    viewport: {
      height: window.innerHeight,
      width: window.innerWidth,
    },
  };
}

function hasPrivacyOptOut() {
  if (typeof window === "undefined" || !window.navigator) {
    return false;
  }

  const { navigator } = window;

  return (
    navigator.globalPrivacyControl === true ||
    navigator.doNotTrack === "1" ||
    navigator.doNotTrack === "yes" ||
    window.doNotTrack === "1" ||
    navigator.msDoNotTrack === "1"
  );
}

export default function VisitorTracker() {
  const location = useLocation();

  useEffect(() => {
    if (hasPrivacyOptOut()) {
      return;
    }

    const trackedRoute = getTrackedRoute(location.pathname);

    if (!trackedRoute) {
      return;
    }

    fetch("/.netlify/functions/track-visitor", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      keepalive: true,
      body: JSON.stringify({
        browser: getBrowserDetails(),
        pageUrl: `${window.location.pathname}${window.location.search}`,
        sessionId: getSessionId(),
        visitId: createVisitId(),
        visitorId: getVisitorId(),
        pagePath: trackedRoute.pagePath,
        routeName: trackedRoute.routeName,
      }),
    }).catch(() => {});
  }, [location.pathname, location.search]);

  return null;
}
